import { Link } from 'react-router-dom';

/**
 * Getting-started guide for advisory firms / channel partners running Clarid
 * across multiple client banks — the engagement lifecycle, start to finish.
 *
 * Single shared source (channel-partner onboarding, Phase 0): rendered both in
 * the How-To hub and in the Organization dashboard's empty state, so the two
 * can never drift. Bank-user onboarding stays with OnboardingWizard /
 * InstitutionSetupWizard; this is the multi-bank consultant path they don't cover.
 */
interface Step {
  title: string;
  body: string;
  link?: { label: string; to: string };
}

const STEPS: Step[] = [
  {
    title: 'Add each client bank as its own workspace',
    body: "Every bank you serve is a separate, isolated workspace. Create one per client and set its primary regulator (FDIC, OCC, Federal Reserve, or NCUA) so each analysis uses the right examination standards.",
    link: { label: 'Manage banks & members', to: '/teams' },
  },
  {
    title: "Set the bank's footprint",
    body: "Define the bank's assessment areas and branches so CRA and fair-lending geography resolve to the right census tracts and markets.",
    link: { label: 'CRA assessment areas', to: '/cra/assessment-areas' },
  },
  {
    title: 'Bring in the data',
    body: 'Upload the HMDA LAR, the ALERT/ILDR loan trial balance, or marketing content. Clarid geocodes and enriches the records automatically — no manual tract lookups.',
    link: { label: 'LAR Builder', to: '/hmda/lar-builder' },
  },
  {
    title: 'Run the analyses',
    body: 'Fair Lending, CRA, HMDA, and Marketing each produce examiner-ready findings — and cross-reference one another, so a marketing gap can cascade into the fair-lending and exam views.',
    link: { label: 'Fair Lending', to: '/fair-lending' },
  },
  {
    title: 'Package the client deliverable',
    body: 'Assemble a single exam-ready package and a board-ready summary for that client — the cross-module report examiners and boards expect, timestamped and hash-verified.',
    link: { label: 'CRA dashboard', to: '/cra' },
  },
  {
    title: 'Move to the next engagement',
    body: "Switch between client banks from the Organization dashboard. Each bank's data stays fully isolated — nothing crosses between engagements.",
  },
  {
    title: 'Keep each engagement current',
    body: 'Scheduled re-runs and threshold alerts maintain each bank’s posture between on-site visits, so you walk into the next review already up to date.',
  },
];

export default function AdvisoryFirmGuide({ className = '' }: { className?: string }) {
  return (
    <ol className={`space-y-4 ${className}`}>
      {STEPS.map((step, i) => (
        <li key={i} className="flex gap-4">
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-teal-600 text-xs font-semibold text-white">
            {i + 1}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">{step.title}</p>
            <p className="mt-0.5 text-sm leading-relaxed text-gray-600">{step.body}</p>
            {step.link && (
              <Link
                to={step.link.to}
                className="mt-1 inline-block text-xs font-medium text-teal-700 hover:text-teal-800 hover:underline"
              >
                {step.link.label} →
              </Link>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
